import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentIllustration,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from '@/components/ModalSheet'
import { type ReactNode } from 'react'
import { Form, FormRow } from '@/components/Form'
import { Button } from '@/components/Button'
import styles from './ReopenJobAsAdminDialog.module.css'
import { ErrorBlock } from '@/components/ErrorBlock'
import { byDesign } from '@/infra/design'
import imgMissingPermissionsUrl from '@/assets/misc/img_missing-permissions.svg'
import JobNoLongerAvailableIllustration from '@/components/SpotIllustration/JobNoLongerAvailableIllustration'

export interface ReopenJobAsAdminDialogProps {
  showing: boolean
  reasonField: ReactNode
  otherReasonField: ReactNode
  reasonHelpText: ReactNode
  isReopenPending: boolean
  isReopenRejected: boolean
  isReopenDisabled: boolean
  reopenError: unknown
  onClose: () => unknown
  onReopen: () => unknown
  submitButtonTestId?: string
}

export function ReopenJobAsAdminDialog({
  showing,
  reasonField,
  otherReasonField,
  reasonHelpText,
  isReopenPending,
  isReopenRejected,
  isReopenDisabled,
  reopenError,
  onClose,
  onReopen,
  submitButtonTestId,
}: ReopenJobAsAdminDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isReopenPending ? undefined : onClose}
    >
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent>
        <div className={styles.contentBlock}>
          <ModalSheetContentIllustration
            src={imgMissingPermissionsUrl}
            illustration={byDesign(undefined, JobNoLongerAvailableIllustration)}
            alt="Illustration of hand holding a STOP symbol"
          />

          <ModalSheetContentTitle compact>
            <span className={styles.title}>
              Are you sure you want to reopen this job?
            </span>
          </ModalSheetContentTitle>

          <ModalSheetContentDescription compact>
            <span className={styles.description}>
              All associated users will be notified of this change
            </span>
          </ModalSheetContentDescription>
        </div>

        <ModalSheetContentBlock>
          <Form>
            <FormRow className={styles.row}></FormRow>
            <FormRow className={styles.row}>
              <div className={styles.reasonLabel}>Reason</div>
              {reasonField}
              {otherReasonField}
              <div className={styles.reasonHelp}>{reasonHelpText}</div>
            </FormRow>
          </Form>
        </ModalSheetContentBlock>
      </ModalSheetContent>

      {isReopenRejected && <ErrorBlock tackOn="bottom" error={reopenError} />}

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            isDisabled={isReopenPending}
            variant="default"
            onPress={onClose}
          />
          <Button
            label="Reopen"
            variant="destructive"
            isPending={isReopenPending}
            isDisabled={isReopenDisabled}
            testId={submitButtonTestId}
            onPress={onReopen}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}
